import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  FormControl,
  IconButton,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Switch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import {
  CUTOFF_HOUR_RANGE,
  CUTOFF_KEY,
  SCHEDULE_SLOT_KEYS,
  SCHEDULE_SLOT_LABELS,
  WEEKDAY_KEYS,
  WEEKDAY_LABELS,
  WEEKDAY_SHORT_LABELS,
  formatCutoffHourOption24h,
  formatCutoffSummary,
  formatCutoffTimeDisplay,
  getCutoffDayOptionLabel,
  normalizeCutoffValue,
  parseCutoffHourFromStoredTime,
} from './constants';
import {
  createAllTrueWeekSchedule,
  generateMockDefaultSchedules,
  readAvailabilityStore,
  writeAvailabilityStore,
} from './storage';

const COPY_NONE = '__none__';
/** Copy source weekday to every other day in the grid (Copy To menu). */
const COPY_ALL_OTHER_DAYS = '__all_other_days__';

/** Shared compact sizing for Copy To selects in the grid. */
const SCHEDULE_GRID_SELECT_SX = {
  minWidth: 128,
  maxWidth: 1,
  width: '100%',
  typography: 'caption',
};

const CUTOFF_DAY_OFFSETS = [0, 1, 2, 3, 4, 5, 6];

/** iOS-style switch from MUI docs: https://mui.com/material-ui/react-switch/ (Customization) */
const IOSSwitch = styled(props => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#65C466',
        opacity: 1,
        border: 0,
        ...theme.applyStyles('dark', {
          backgroundColor: '#2ECA45',
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[600],
      }),
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
      ...theme.applyStyles('dark', {
        opacity: 0.3,
      }),
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
    ...theme.applyStyles('dark', {
      backgroundColor: '#39393D',
    }),
  },
}));

function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function normalizeSchedule(s) {
  const base = createAllTrueWeekSchedule();
  if (!s || typeof s !== 'object') return base;
  WEEKDAY_KEYS.forEach(wk => {
    SCHEDULE_SLOT_KEYS.forEach(sk => {
      if (typeof s[wk]?.[sk] === 'boolean') {
        base[wk][sk] = s[wk][sk];
      }
    });
    base[wk][CUTOFF_KEY] = normalizeCutoffValue(s[wk]?.[CUTOFF_KEY], wk);
  });
  return base;
}

function loadSchedulesFromPropOrStorage(value) {
  if (Array.isArray(value) && value.length > 0) {
    return value.map(r => ({
      id: r.id,
      effectiveDate: r.effectiveDate,
      schedule: normalizeSchedule(r.schedule),
    }));
  }
  const stored = readAvailabilityStore().defaultSchedules;
  if (Array.isArray(stored) && stored.length > 0) {
    return stored.map(r => ({
      id: r.id,
      effectiveDate: r.effectiveDate,
      schedule: normalizeSchedule(r.schedule),
    }));
  }
  const mock = generateMockDefaultSchedules();
  writeAvailabilityStore({ defaultSchedules: mock });
  return clone(mock);
}

function effectiveDateIsFuture(effectiveDateStr) {
  const ed = dayjs(effectiveDateStr).startOf('day');
  const today = dayjs().startOf('day');
  return ed.isAfter(today, 'day');
}

function formatEffectiveDateFriendly(isoDate) {
  const d = dayjs(isoDate);
  if (!d.isValid()) return isoDate;
  return d.format('dddd, MMMM D, YYYY');
}

/**
 * @param {{ value?: unknown[], onSchedulesChange?: (rows: unknown[]) => void }} props
 */
function DefaultScheduling({ value, onSchedulesChange, setPanel }) {
  const baselineRef = useRef(null);
  /** Grid starts read-only; list row Edit only for future effective dates (current is view-only). */
  const [isEditing, setIsEditing] = useState(false);
  const [schedules, setSchedules] = useState(() => {
    const rows = loadSchedulesFromPropOrStorage(value);
    baselineRef.current = clone(rows);
    return rows;
  });

  const [draft, setDraft] = useState(() => {
    const rows = loadSchedulesFromPropOrStorage(value);
    const first = rows[0];
    return {
      id: first?.id || 'default',
      effectiveDate: first?.effectiveDate || dayjs().format('YYYY-MM-DD'),
      schedule: normalizeSchedule(first?.schedule),
    };
  });

  const [copySelections, setCopySelections] = useState(() =>
    Object.fromEntries(WEEKDAY_KEYS.map(wk => [wk, COPY_NONE])),
  );
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cutoffDialog, setCutoffDialog] = useState(null);

  useEffect(() => {
    if (value !== undefined && Array.isArray(value)) {
      const rows = loadSchedulesFromPropOrStorage(value);
      baselineRef.current = clone(rows);
      setSchedules(rows);
      const first = rows[0];
      if (first) {
        setDraft({
          id: first.id,
          effectiveDate: first.effectiveDate,
          schedule: normalizeSchedule(first.schedule),
        });
      }
    }
  }, [value]);

  useEffect(() => {
    if (isEditing && !effectiveDateIsFuture(draft.effectiveDate)) {
      setIsEditing(false);
    }
  }, [isEditing, draft.effectiveDate]);

  const setSlot = useCallback((weekday, slotKey, checked) => {
    setDraft(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [weekday]: {
          ...prev.schedule[weekday],
          [slotKey]: checked,
        },
      },
    }));
  }, []);

  const applyCopyFromWeekday = useCallback((fromWk, toWk) => {
    setDraft(prev => {
      const nextSch = clone(prev.schedule);
      const targetDays =
        toWk === COPY_ALL_OTHER_DAYS
          ? WEEKDAY_KEYS.filter(w => w !== fromWk)
          : [toWk];
      targetDays.forEach(tw => {
        SCHEDULE_SLOT_KEYS.forEach(sk => {
          nextSch[tw][sk] = nextSch[fromWk][sk];
        });
        const srcCut = normalizeCutoffValue(
          nextSch[fromWk][CUTOFF_KEY],
          fromWk,
        );
        nextSch[tw][CUTOFF_KEY] = {
          daysBefore: srcCut.daysBefore,
          time: srcCut.time,
        };
      });
      return { ...prev, schedule: nextSch };
    });
    setCopySelections(
      Object.fromEntries(WEEKDAY_KEYS.map(wk => [wk, COPY_NONE])),
    );
  }, []);

  const openCutoffDialog = useCallback(
    wk => {
      const c = normalizeCutoffValue(draft.schedule[wk]?.[CUTOFF_KEY], wk);
      setCutoffDialog({
        weekday: wk,
        daysBefore: c.daysBefore,
        cutoffHour: parseCutoffHourFromStoredTime(c.time),
      });
    },
    [draft.schedule],
  );

  const applyCutoffDialog = useCallback(() => {
    if (!cutoffDialog) return;
    const { weekday, daysBefore, cutoffHour } = cutoffDialog;
    let h = Number(cutoffHour);
    if (!Number.isInteger(h) || h < 0 || h > 23) {
      h = 12;
    }
    const time = `${String(h).padStart(2, '0')}:00`;
    setDraft(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [weekday]: {
          ...prev.schedule[weekday],
          [CUTOFF_KEY]: {
            daysBefore,
            time,
          },
        },
      },
    }));
    setCutoffDialog(null);
  }, [cutoffDialog]);

  const handleSave = () => {
    const effectiveDate = dayjs(draft.effectiveDate).format('YYYY-MM-DD');
    const payload = {
      id: draft.id || nanoid(),
      effectiveDate,
      schedule: clone(draft.schedule),
    };
    const idx = schedules.findIndex(r => r.id === payload.id);
    let nextRows;
    if (idx >= 0) {
      nextRows = [...schedules];
      nextRows[idx] = payload;
    } else {
      nextRows = [...schedules, payload];
    }
    setSchedules(nextRows);
    writeAvailabilityStore({ defaultSchedules: nextRows });
    baselineRef.current = clone(nextRows);
    onSchedulesChange?.(nextRows);
    setIsEditing(false);
  };

  const handleCancel = () => {
    const base = baselineRef.current
      ? clone(baselineRef.current)
      : loadSchedulesFromPropOrStorage(value);
    setSchedules(base);
    const first = base[0];
    setDraft({
      id: first?.id || 'default',
      effectiveDate: first?.effectiveDate || dayjs().format('YYYY-MM-DD'),
      schedule: normalizeSchedule(first?.schedule),
    });
    setIsEditing(false);
  };

  const handleReset = () => {
    const saved = schedules.find(r => r.id === draft.id);
    if (saved) {
      setDraft({
        id: saved.id,
        effectiveDate: saved.effectiveDate,
        schedule: clone(saved.schedule),
      });
    } else {
      setDraft(prev => ({
        ...prev,
        schedule: createAllTrueWeekSchedule(),
      }));
    }
  };

  const selectScheduleInGrid = useCallback(row => {
    setDraft({
      id: row.id,
      effectiveDate: row.effectiveDate,
      schedule: normalizeSchedule(row.schedule),
    });
    setIsEditing(false);
    setCopySelections(
      Object.fromEntries(WEEKDAY_KEYS.map(wk => [wk, COPY_NONE])),
    );
  }, []);

  const openEdit = row => {
    if (!effectiveDateIsFuture(row.effectiveDate)) return;
    setDraft({
      id: row.id,
      effectiveDate: row.effectiveDate,
      schedule: normalizeSchedule(row.schedule),
    });
    setIsEditing(true);
  };

  const handleCreateNew = useCallback(() => {
    setDraft({
      id: nanoid(),
      effectiveDate: dayjs().add(1, 'day').format('YYYY-MM-DD'),
      schedule: createAllTrueWeekSchedule(),
    });
    setIsEditing(true);
    setCopySelections(
      Object.fromEntries(WEEKDAY_KEYS.map(wk => [wk, COPY_NONE])),
    );
  }, []);

  const confirmDelete = () => {
    if (!deleteTarget) return;
    if (!effectiveDateIsFuture(deleteTarget.effectiveDate)) {
      setDeleteTarget(null);
      return;
    }
    const nextRows = schedules.filter(r => r.id !== deleteTarget.id);
    setSchedules(nextRows);
    writeAvailabilityStore({ defaultSchedules: nextRows });
    baselineRef.current = clone(nextRows);
    onSchedulesChange?.(nextRows);
    setDeleteTarget(null);
    if (draft.id === deleteTarget.id) {
      const first = nextRows[0];
      setDraft(
        first
          ? {
              id: first.id,
              effectiveDate: first.effectiveDate,
              schedule: normalizeSchedule(first.schedule),
            }
          : {
              id: nanoid(),
              effectiveDate: dayjs().format('YYYY-MM-DD'),
              schedule: createAllTrueWeekSchedule(),
            },
      );
    }
  };

  const displaySchedules = useMemo(() => {
    const rows = [...schedules];
    const currents = rows.filter(r => !effectiveDateIsFuture(r.effectiveDate));
    const futures = rows.filter(r => effectiveDateIsFuture(r.effectiveDate));
    currents.sort(
      (a, b) =>
        dayjs(b.effectiveDate).valueOf() - dayjs(a.effectiveDate).valueOf(),
    );
    futures.sort(
      (a, b) =>
        dayjs(a.effectiveDate).valueOf() - dayjs(b.effectiveDate).valueOf(),
    );
    return [...currents, ...futures];
  }, [schedules]);

  const showEditingControls =
    isEditing && effectiveDateIsFuture(draft.effectiveDate);

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}>
          <Typography variant="h6" component="h2">
            Default Schedule
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              onClick={() => {
                setPanel('calendar');
              }}>
              Live Calendar
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setPanel('custom');
              }}>
              Special Schedules
            </Button>
          </Stack>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        {!showEditingControls && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
              Period:
            </Typography>
            <Typography variant="body1">
              {effectiveDateIsFuture(draft.effectiveDate)
                ? 'Future'
                : 'Current'}
            </Typography>
          </Stack>
        )}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
            Effective date:
          </Typography>
          {showEditingControls ? (
            <DatePicker
              disablePast
              value={dayjs(draft.effectiveDate)}
              onChange={v => {
                if (v) {
                  setDraft(prev => ({
                    ...prev,
                    effectiveDate: v.format('YYYY-MM-DD'),
                  }));
                }
              }}
              slotProps={{
                textField: {
                  id: 'default-schedule-effective',
                  size: 'small',
                  sx: { minWidth: 200 },
                },
              }}
            />
          ) : (
            <Typography variant="body1">
              {formatEffectiveDateFriendly(draft.effectiveDate)}
            </Typography>
          )}
        </Stack>
        <Typography variant="subtitle2" sx={{ flexShrink: 0, mb: 1 }}>
          Schedule:
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <TableContainer>
            <Table
              size="small"
              sx={{
                borderCollapse: 'collapse',
                '& .MuiTableCell-root': { border: 'none', py: 1.25, px: 1 },
              }}>
              <TableHead>
                <TableRow>
                  <TableCell />
                  {WEEKDAY_KEYS.map(wk => (
                    <TableCell key={wk} align="center">
                      {showEditingControls ? (
                        <Stack
                          spacing={1}
                          alignItems="center"
                          sx={{ width: '100%' }}>
                          <Typography variant="subtitle2">
                            {WEEKDAY_SHORT_LABELS[wk]}
                          </Typography>
                          <Select
                            size="small"
                            variant="outlined"
                            displayEmpty
                            value={copySelections[wk]}
                            onChange={e => {
                              const toWk = e.target.value;
                              if (
                                toWk &&
                                toWk !== COPY_NONE &&
                                (toWk === COPY_ALL_OTHER_DAYS ||
                                  WEEKDAY_KEYS.includes(toWk))
                              ) {
                                applyCopyFromWeekday(wk, toWk);
                              }
                            }}
                            sx={{
                              ...SCHEDULE_GRID_SELECT_SX,
                              width: '60%',
                              minWidth: '100px',
                            }}
                            renderValue={selected => {
                              if (!selected || selected === COPY_NONE) {
                                return (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary">
                                    Copy To
                                  </Typography>
                                );
                              }
                              if (selected === COPY_ALL_OTHER_DAYS) {
                                return (
                                  <Typography variant="caption">
                                    All Other days
                                  </Typography>
                                );
                              }
                              return WEEKDAY_LABELS[selected];
                            }}>
                            <MenuItem
                              value={COPY_NONE}
                              sx={{ display: 'none' }}>
                              {' '}
                            </MenuItem>
                            <MenuItem value={COPY_ALL_OTHER_DAYS}>
                              All Other days
                            </MenuItem>
                            {WEEKDAY_KEYS.filter(x => x !== wk).map(target => (
                              <MenuItem key={target} value={target}>
                                {WEEKDAY_LABELS[target]}
                              </MenuItem>
                            ))}
                          </Select>
                        </Stack>
                      ) : (
                        <Typography variant="subtitle2">
                          {WEEKDAY_SHORT_LABELS[wk]}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {SCHEDULE_SLOT_KEYS.map(sk => (
                  <TableRow key={sk}>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        {SCHEDULE_SLOT_LABELS[sk]}
                      </Typography>
                    </TableCell>
                    {WEEKDAY_KEYS.map(wk => {
                      const slotOn = Boolean(draft.schedule[wk]?.[sk]);
                      const aria = `${SCHEDULE_SLOT_LABELS[sk]} ${WEEKDAY_LABELS[wk]}`;
                      let slotContent;
                      if (showEditingControls) {
                        slotContent = (
                          <IOSSwitch
                            checked={slotOn}
                            onChange={e => setSlot(wk, sk, e.target.checked)}
                            slotProps={{
                              input: { 'aria-label': aria },
                            }}
                          />
                        );
                      } else if (slotOn) {
                        slotContent = (
                          <CheckCircle
                            fontSize="small"
                            sx={{
                              color: 'success.main',
                              verticalAlign: 'middle',
                            }}
                            aria-label={`${aria}: available`}
                          />
                        );
                      } else {
                        slotContent = (
                          <Cancel
                            fontSize="small"
                            sx={{
                              color: 'error.main',
                              verticalAlign: 'middle',
                            }}
                            aria-label={`${aria}: unavailable`}
                          />
                        );
                      }
                      return (
                        <TableCell key={`${wk}-${sk}`} align="center">
                          {slotContent}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell align="right">
                    <Typography variant="subtitle2">Cutoff Time</Typography>
                  </TableCell>
                  {WEEKDAY_KEYS.map(wk => (
                    <TableCell key={`${wk}-cutoff`} align="center">
                      {showEditingControls ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => openCutoffDialog(wk)}
                          sx={{
                            maxWidth: '100%',
                            typography: 'caption',
                            whiteSpace: 'normal',
                            textAlign: 'center',
                          }}>
                          {formatCutoffSummary(
                            wk,
                            draft.schedule[wk]?.[CUTOFF_KEY],
                          )}
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {formatCutoffSummary(
                            wk,
                            draft.schedule[wk]?.[CUTOFF_KEY],
                          )}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {showEditingControls && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleSave}>
                Save
              </Button>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="text" onClick={handleReset}>
                Reset
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>

      {!showEditingControls && (
        <Paper variant="outlined" sx={{ p: 2, mt: 4 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ mb: 1 }}>
            <Typography
              color="primary"
              variant="subtitle1"
              sx={{ fontWeight: 500 }}>
              Future Default Schedules
            </Typography>
            <Button
              variant="contained"
              size="small"
              sx={{ flexShrink: 0 }}
              onClick={handleCreateNew}>
              New Default Schedule
            </Button>
          </Stack>

          <TableContainer>
            <Table size="small" sx={{ mt: 1.5 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 88 }} />
                  <TableCell>Effective date</TableCell>
                  <TableCell>Period</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary">
                        No schedules yet. Use Edit below the grid to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {displaySchedules.map(row => (
                  <TableRow key={row.id} hover selected={row.id === draft.id}>
                    <TableCell
                      align="left"
                      sx={{ width: 88, whiteSpace: 'nowrap' }}>
                      {effectiveDateIsFuture(row.effectiveDate) && (
                        <>
                          <IconButton
                            size="small"
                            aria-label="Edit schedule"
                            onClick={() => openEdit(row)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete schedule"
                            onClick={() => setDeleteTarget(row)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                    <TableCell
                      onClick={() => selectScheduleInGrid(row)}
                      sx={{ cursor: 'pointer' }}>
                      {row.effectiveDate}
                    </TableCell>
                    <TableCell>
                      {effectiveDateIsFuture(row.effectiveDate) ? (
                        <Typography variant="body2">Future</Typography>
                      ) : (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}>
                          <Typography variant="body2" component="span">
                            Current
                          </Typography>
                          <CheckCircle
                            fontSize="small"
                            sx={{ color: 'success.main' }}
                            aria-hidden
                          />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      <Dialog
        open={Boolean(cutoffDialog)}
        onClose={() => setCutoffDialog(null)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Cutoff Time</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              To accept jobs on{' '}
              {cutoffDialog ? WEEKDAY_LABELS[cutoffDialog.weekday] : ''}, my
              cutoff time is:
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="cutoff-day-label">Cutoff Day</InputLabel>
              <Select
                labelId="cutoff-day-label"
                label="Cutoff Day"
                value={cutoffDialog?.daysBefore ?? 0}
                onChange={e =>
                  setCutoffDialog(prev =>
                    prev
                      ? { ...prev, daysBefore: Number(e.target.value) }
                      : prev,
                  )
                }>
                {cutoffDialog &&
                  CUTOFF_DAY_OFFSETS.map(off => (
                    <MenuItem key={off} value={off}>
                      {getCutoffDayOptionLabel(cutoffDialog.weekday, off)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ width: 104, flexShrink: 0 }}>
                <InputLabel id="cutoff-time-hour-label">Cutoff Time</InputLabel>
                <Select
                  labelId="cutoff-time-hour-label"
                  label="Cutoff Time"
                  value={cutoffDialog?.cutoffHour ?? 12}
                  onChange={e =>
                    setCutoffDialog(prev =>
                      prev
                        ? { ...prev, cutoffHour: Number(e.target.value) }
                        : prev,
                    )
                  }>
                  {CUTOFF_HOUR_RANGE.map(hour => (
                    <MenuItem key={hour} value={hour}>
                      {formatCutoffHourOption24h(hour)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {cutoffDialog != null && (
                <Box sx={{ pt: 3 }}>
                  <Typography variant="subtitle2" color="black">
                    {formatCutoffTimeDisplay(
                      formatCutoffHourOption24h(cutoffDialog.cutoffHour),
                    )}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
          <Button onClick={() => setCutoffDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={applyCutoffDialog}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete schedule</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove the schedule effective {deleteTarget?.effectiveDate}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>No</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DefaultScheduling;
